import React, { useEffect, useState } from 'react';
import { AlertOctagon, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

/**
 * Founder view-as session indicator.
 *
 * Activates when the live ID-token claim `isFounder === true` AND the URL has
 * a valid `?viewAs=<companyId>` param (validated by AuthContext). Renders a
 * sticky red banner above all dashboard content with a 15-minute auto-expiry
 * timer; on expiry the founder is signed out and redirected to the login page
 * with a flag so the admin Console can re-anchor.
 *
 * Read-only safety is NOT enforced by this banner — it is enforced at the
 * write boundary by `blockFounderClientWrite` on every mutation callable.
 * The banner is purely a visual reminder + an explicit exit affordance.
 *
 * @see apps/client/src/context/AuthContext.tsx (viewAs branch)
 * @see packages/shared-utils/src/auth/blockFounderClientWrite.ts (write block)
 */

const VIEW_AS_TIMEOUT_MS = 15 * 60 * 1000;

const formatRemaining = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const ViewAsBanner: React.FC = () => {
  const { viewAsActive, viewAsTargetCompanyId, logout } = useAuth();
  const [remainingMs, setRemainingMs] = useState(VIEW_AS_TIMEOUT_MS);

  useEffect(() => {
    if (!viewAsActive) return;

    const startedAt = Date.now();
    const tick = () => {
      const elapsed = Date.now() - startedAt;
      const remaining = VIEW_AS_TIMEOUT_MS - elapsed;
      if (remaining <= 0) {
        // Auto-expire: sign out and redirect with marker so admin Console
        // can prompt the founder to re-launch view-as if still needed.
        logout()
          .catch(() => {})
          .finally(() => {
            window.location.href = '/login?viewAsExpired=true';
          });
        return;
      }
      setRemainingMs(remaining);
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [viewAsActive, logout]);

  const handleExit = () => {
    // Strip `?viewAs=` and reload — full reload re-bootstraps AuthContext so
    // the founder lands back on their own (admin) session without leaking
    // the target tenant's `companyId` into a stale React context.
    const url = new URL(window.location.href);
    url.searchParams.delete('viewAs');
    window.location.href = url.pathname + url.search + url.hash;
  };

  if (!viewAsActive) return null;

  return (
    <div className="sticky top-0 z-40 mx-4 mt-4 md:mx-6 md:mt-6 mb-0">
      <div
        role="status"
        aria-live="polite"
        className="relative flex flex-wrap items-center gap-3 px-4 py-3 rounded-lg bg-red-900/40 border-2 border-red-600 text-red-100 shadow-lg"
      >
        <AlertOctagon className="flex-shrink-0 text-red-400" size={20} aria-hidden />
        <div className="flex-1 min-w-0 text-sm">
          <span className="font-semibold uppercase tracking-wide">Founder view-as</span>
          <span className="mx-2 text-red-300/60">·</span>
          <span className="font-mono break-all">{viewAsTargetCompanyId}</span>
          <span className="mx-2 text-red-300/60">·</span>
          <span>read-only</span>
          <span className="mx-2 text-red-300/60">·</span>
          <span>audited</span>
          <span className="mx-2 text-red-300/60">·</span>
          <span>
            expires in <span className="font-mono">{formatRemaining(remainingMs)}</span>
          </span>
        </div>
        <button
          type="button"
          onClick={handleExit}
          className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-700 hover:bg-red-600 text-white rounded-md transition-colors"
          aria-label="Exit founder view-as session"
        >
          <X size={14} aria-hidden />
          Exit view-as
        </button>
      </div>
    </div>
  );
};

export default ViewAsBanner;
